import invariant from 'tiny-invariant'
import { v4 as uuidv4 } from 'uuid'

import type { definitions } from '@luxuryescapes/contract-svc-accommodation'

import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import config from 'constants/config'
import { arrayToObject, isNonEmptyArray } from 'lib/array/arrayUtils'
import { mapObject } from 'lib/object/objectUtils'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import { ICONS } from 'lib/offer/getPropertyIcon'
import { getMaxNonInfantGuestCount, mapAgeCategories } from 'lib/offer/occupancyUtils'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'
import { stripMarkdown } from 'lib/string/stripMarkdown'

import { mapPromotionType } from './propertySharedMapper'

/* ***** */
/* Utils */
/* ***** */

function mapNonEmptyArray<T, U>(values: Array<T>, map: (value: T) => U | null | undefined): Utils.NonEmptyArray<U> | undefined {
  const mappedValues = values.flatMap((value) => {
    const mappedValue = map(value)
    return mappedValue != null ? [mappedValue] : []
  })
  if (!isNonEmptyArray(mappedValues)) return
  return mappedValues
}

function mapOptional<T, U>(value: T | null | undefined, map: (value: T) => U): U | undefined {
  return value != null ? map(value) : undefined
}

/* *********** */
/* Type guards */
/* *********** */

const BED_SIZES: Record<App.Property.BedSize, undefined> = { 'KING': undefined, 'QUEEN': undefined, 'DOUBLE': undefined, 'FULL': undefined, 'TWIN': undefined, 'TWIN_XL': undefined, 'SINGLE': undefined, 'ROLLAWAY': undefined, 'VARIABLE': undefined, 'SEMI': undefined }
const BED_TYPES: Record<App.Property.BedType, undefined> = { 'KING_BED': undefined, 'QUEEN_BED': undefined, 'DOUBLE_BED': undefined, 'FULL_BED': undefined, 'SINGLE_BED': undefined, 'TWIN_BED': undefined, 'TWIN_XL_BED': undefined, 'SOFA_BED': undefined, 'DOUBLE_SOFA_BED': undefined, 'QUEEN_SOFA_BED': undefined, 'BUNK_BED': undefined, 'ROLL_AWAY_BED': undefined, 'DAY_BED': undefined, 'MURPHY_BED': undefined, 'TRUNDLE_BED': undefined, 'FUTON': undefined, 'WATER_BED': undefined, 'OTHER': undefined, 'FUTONBED': undefined }

function isBedSize(value: string): value is App.Property.BedSize {
  return Object.hasOwn(BED_SIZES, value)
}

function isBedType(value: string): value is App.Property.BedType {
  return Object.hasOwn(BED_TYPES, value)
}

/* *********** */
/* Sub-mappers */
/* *********** */

type ServerAgeCategory = definitions['AgeCategory']
type ServerAmenity = definitions['Amenity']
type ServerAmenityGroup = definitions['AmenityGroup']
type ServerContent = definitions['Content']
type ServerDuration = definitions['Duration']
type ServerFinePrintSubsection = definitions['FinePrintSubsection']
type ServerGroupedFacility = definitions['GroupedFacility']
type ServerGroupedFacilityCatalogGroup = definitions['GroupedFacilityCatalogGroup']
type ServerGroupedFacilityGroup = definitions['GroupedFacilityGroup']
type ServerIcon = definitions['Icon']
type ServerImage = definitions['Image']
type ServerOccupancy = definitions['Occupancy']
type ServerPopularFacility = definitions['PopularFacility']
type ServerPropertyLocation = definitions['PropertyLocation']
type ServerPropertyPromotion = NonNullable<definitions['PropertyContent']['promotion']>
type ServerPropertyReviews = definitions['PropertyReviews']
type ServerRoomBedConfiguration = definitions['RoomBedConfiguration']
type ServerRoomBedGroup = definitions['RoomBedGroup']
type ServerRoomConfiguration = definitions['RoomConfiguration']
type ServerRoomConfigurationBed = definitions['RoomConfigurationBed']
type ServerRoomInclusion = definitions['RoomInclusion']
type ServerRoomPromotion = definitions['RoomPromotion']
type ServerStats = definitions['Stats']
type ServerStructuredFinePrint = definitions['StructuredFinePrint']

function mapAgeCategory({
  type,
  // name, // We'll use the normalised `type` instead.
  minimumAge,
}: ServerAgeCategory): App.PackageRoomAgeCategory {
  switch (type) {
    case 'adult':
      return { name: 'Adult', minimumAge }
    case 'child':
      return { name: 'Child', minimumAge }
    case 'teenager':
      return { name: 'Teenager', minimumAge }
    case 'infant':
      return { name: 'Infant', minimumAge }
    default:
      assertUnreachable(type)
  }
}

function mapAmenityGroup({ name, values }: ServerAmenityGroup): App.Property.AmenityGroup {
  return {
    name,
    items: values.map(mapAmenityItem),
  }
}

function mapAmenityItem({ name }: ServerAmenity): App.Property.AmenityItem {
  return { id: name, name, iconName: undefined }
}

function mapBedConfiguration({
  bedTypeCode,
  // bedTypeName, // This is unused.
  bedSizeCode,
  // bedSizeName, // This is unused.
  bedTypeIcon,
  quantity,
}: ServerRoomBedConfiguration): App.Property.BedConfiguration | undefined {
  if (!isBedType(bedTypeCode)) return
  return {
    type: bedTypeCode,
    size: bedSizeCode && isBedSize(bedSizeCode) ? bedSizeCode : undefined,
    quantity,
    iconName: mapIconName(bedTypeIcon),
  }
}

function mapBedGroup({
  id,
  configurations: serverConfigurations,
}: ServerRoomBedGroup): App.Property.BedGroup | undefined {
  if (!id) return
  const configurations = mapNonEmptyArray(serverConfigurations, mapBedConfiguration)
  if (!configurations) return
  return { id, configurations }
}

function mapCapacities({
  adults,
  teenagers,
  children,
  infants,
}: ServerOccupancy): App.RoomCapacity {
  return { adults, teenagers, children, infants }
}

function mapDurationOption({
  duration,
  hasBonusInclusions,
  hasTactical,
  promotion,
}: ServerDuration): App.Property.DurationOption {
  return {
    duration,
    hasBonusInclusions,
    hasTactical,
    promotion: promotion ? {
      ...mapPromotionType(promotion.type),
      travelFromIsoDate: promotion.travelPeriod.from,
      travelToIsoDate: promotion.travelPeriod.to,
    } : undefined,
  }
}

function mapFinePrintContent({ type, value }: ServerContent): App.Property.FinePrintContent {
  invariant(type === 'text' || type === 'markdown', 'Expected text or Markdown content type')
  return { type, value }
}

function mapFinePrintSubsection({
  key,
  icon,
  label,
  content,
  collapsable,
}: ServerFinePrintSubsection): App.Property.FinePrintSubsection {
  return {
    key,
    icon,
    label,
    content: mapFinePrintContent(content),
    collapsable,
  }
}

function mapIconName(icon: ServerIcon): App.Property.IconName | undefined {
  return ICONS[icon] ? icon : undefined
}

function mapImage({ id, title }: ServerImage): App.Image {
  return { id, title: title ?? undefined }
}

function mapLocation({
  coordinates,
  timezone,
  timezoneOffset,
  address,
  streetAddress,
  city,
  region,
  countryName,
  countryCode,
  continentCode,
  placeId,
}: ServerPropertyLocation, title: ServerPropertyContent['locationTitle']): App.Property.Location {
  return {
    timezone,
    timezoneOffset: timezoneOffset ?? undefined,
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    title: title ?? undefined,
    formattedAddress: address,
    streetAddress: streetAddress ?? undefined,
    city: city ?? undefined,
    region: region ?? undefined,
    countryName: countryName ?? undefined,
    countryCode: countryCode ?? undefined,
    continentCode: continentCode ?? undefined,
    placeId: placeId ?? undefined,
  }
}

function mapMarkdown({ type, value }: ServerContent): string {
  // We don't have a static assertion, as `type` is allowed to take other values. However, in
  // practice, accommodation service won't be serving `'html'` for a while.
  invariant(type === 'markdown' || type === 'text', 'Expected Markdown (or text) content type')
  // Whilst we don't expect `'text'` for content that accommodation service proxies through public
  // offer service, it _may_ be returned for content directly managed by accommodation service. For
  // now, we'll tolerate both types. Ultimately, we need to identify which fields are text-only,
  // which are Markdown-only, and which can legitimately be both.
  if (type === 'text') return stripMarkdown(value)
  return value
}

function mapPopularFacility({
  code,
  name,
  icon,
}: ServerPopularFacility): App.Property.AmenityItem {
  return {
    id: code,
    name,
    iconName: mapIconName(icon),
  }
}

function mapPropertyPromotion({
  type,
  // name, // This is unused (for now).
  // subtitle, // This is unused (for now).
  activePeriod: {
    to: endIsoDateTime,
  },
  travelPeriod: {
    from: travelFromIsoDate,
    to: travelToIsoDate,
  },
  minimumLos,
  // sourceId, // This is unused (for now).
  // marketedLos, // This is unused.
  // defaultOccupancy, // This is unused.
}: ServerPropertyPromotion): App.Property.PropertyPromotion {
  return {
    ...mapPromotionType(type),
    endIsoDateTime,
    travelFromIsoDate,
    travelToIsoDate,
    minimumLos,
  }
}

function mapRating({ rating, count, source, ratingBreakdown }: ServerPropertyReviews): App.OfferRating {
  return {
    score: rating,
    reviewsTotal: count,
    reviewsSource: source,
    reviewsRatingBreakdown: ratingBreakdown,
  }
}

function mapRoomConfiguration({
  beds,
  roomTypeDescription,
}: ServerRoomConfiguration): App.RoomConfiguration {
  return {
    beds: beds.map(mapRoomConfigurationBed),
    name: roomTypeDescription,
  }
}

function mapRoomConfigurationBed({
  bedType,
  bedTypeDescription,
  count,
}: ServerRoomConfigurationBed): App.RoomConfigurationBed {
  return {
    type: bedType,
    name: bedTypeDescription,
    count,
  }
}

function mapRoomInclusion({
  // code, // This isn't guaranteed to be unique.
  // name, // This shouldn't be displayed.
  text,
  icon,
  // unit, // This cannot be mapped to `App.OfferInclusion`.
  minLos,
  maxLos,
}: ServerRoomInclusion, type: 'standard' | 'bonus' | 'luxPlus'): App.OfferInclusion {
  return {
    id: uuidv4(),
    description: text,
    symbol: determineOfferFeatureSymbol(icon || text),
    isBonus: type === 'bonus',
    isLuxPlusBonus: type === 'luxPlus',
    minDuration: minLos ?? undefined,
    maxDuration: maxLos ?? undefined,
  }
}

function mapRoomInclusions(
  type: 'standard' | 'bonus' | 'luxPlus',
): (serverRoomInclusions: Array<ServerRoomInclusion>) => Array<App.OfferInclusion> {
  return (serverRoomInclusions) =>
    serverRoomInclusions.map(serverRoomInclusion => mapRoomInclusion(serverRoomInclusion, type))
}

function mapRoomPromotion({
  type,
  name,
  travelPeriod: {
    from: travelFromIsoDate,
    to: travelToIsoDate,
  },
  minimumLos,
}: ServerRoomPromotion): App.Property.RoomPromotion {
  return {
    ...mapPromotionType(type),
    name,
    travelFromIsoDate,
    travelToIsoDate,
    minimumLos,
  }
}

function mapSingleUnitContent(rooms: Array<ServerRoomContent>): App.Property.SingleUnitPropertyContent | undefined {
  // TODO(Xiao): Move these checks into accommodation service.
  if (rooms.length !== 1) return
  const maxNonInfantOccupants = getMaxNonInfantGuestCount(rooms[0].occupancies)
  if (!maxNonInfantOccupants) return

  return {
    maxNonInfantOccupants,
    bedrooms: rooms[0].numberOfBedrooms,
    bedGroups: mapNonEmptyArray(rooms[0].bedGroups, mapBedGroup),
    bathrooms: rooms[0].numberOfBathrooms ?? 1, // Nish agreed to this fallback value.
  }
}

function mapStats({ purchaseCount, viewCount }: ServerStats): App.OfferStats {
  return { booked: purchaseCount, views: viewCount }
}

function mapStructuredFacilityGroup({
  group,
  facilities,
}: ServerGroupedFacilityGroup): App.StructuredFacilityGroup {
  return {
    group: mapStructuredFacilityItem(group),
    facilities: facilities.map(mapStructuredFacilityItem),
  }
}

function mapStructuredFacilityItem({
  code,
  name,
  icon,
  displayOrder,
}: ServerGroupedFacility | ServerGroupedFacilityCatalogGroup): App.StructuredFacilityItem {
  return {
    code,
    name,
    iconName: mapIconName(icon),
    displayOrder: displayOrder ?? undefined,
  }
}

function mapStructuredFinePrint({
  checkIn,
  checkOut,
  fees,
  general,
  pets,
  policies,
  safety,
}: ServerStructuredFinePrint, themes: Array<string>): App.BedbankPropertyFinePrint {
  const structuredFinePrint: App.BedbankPropertyFinePrint = {
    general,
    pets,
    policies: mapOptional(policies, x => mapObject(x, mapMarkdown)),
    safety,
    themes,
  }
  if (checkIn.beginTime || checkIn.endTime || checkIn.instructions || checkIn.specialInstructions) {
    structuredFinePrint.checkIn = {
      beginTime: checkIn.beginTime,
      endTime: checkIn.endTime,
      instructions: mapOptional(checkIn.instructions, mapMarkdown),
      specialInstructions: mapOptional(checkIn.specialInstructions, mapMarkdown),
    }
  }
  if (checkOut.time) {
    structuredFinePrint.checkOut = {
      time: checkOut.time,
    }
  }
  if (fees?.mandatory || fees?.optional) {
    structuredFinePrint.fees = {
      mandatory: mapOptional(fees.mandatory, mapMarkdown),
      optional: mapOptional(fees.optional, mapMarkdown),
    }
  }
  return structuredFinePrint
}

/* ************ */
/* Main mappers */
/* ************ */

type ServerPropertyContent = definitions['PropertyContent']
type ServerRoomContent = definitions['RoomContent']

interface MapRoomOptions {
  defaultOrder: number
  previewMode: boolean
}

function mapRoom({
  id,
  name,
  copy: {
    description,
    roomPolicy,
  },
  images,
  amenityGroups,
  popularAmenities,
  roomSize,
  numberOfBedrooms,
  numberOfBathrooms,
  bedGroupDescriptions,
  bedGroups,
  roomConfigurations,
  occupancies,
  stats,
  promotion,
  inclusions,
  bundleInclusions,
  bonusInclusions,
  luxPlusInclusions,
  ageCategories,
  isMysteryRoom,
  shouldHideUnlessHasRates,
}: ServerRoomContent, {
  defaultOrder,
  previewMode,
}: MapRoomOptions): App.Property.Room {
  return {
    /* Identifiers */
    id,
    /* Status flags */
    isMysteryRoom,
    shouldHideUnlessHasRates: !previewMode && shouldHideUnlessHasRates,
    /* Configuration options */
    ageCategoryThresholds: mapAgeCategories(ageCategories.map(mapAgeCategory)),
    capacities: occupancies.map(mapCapacities),
    /* Content */
    name,
    images: images.map(mapImage),
    description: mapOptional(description, mapMarkdown),
    bedrooms: numberOfBedrooms,
    bedGroups: mapNonEmptyArray(bedGroups, mapBedGroup),
    bedGroupDescriptions,
    roomConfigurations: roomConfigurations.map(mapRoomConfiguration),
    bathrooms: numberOfBathrooms ?? undefined,
    size: roomSize ?? undefined,
    popularAmenities: popularAmenities.map(mapPopularFacility),
    amenities: amenityGroups.map(mapAmenityGroup),
    policy: mapOptional(roomPolicy, mapMarkdown),
    /* Inclusions */
    promotion: mapOptional(promotion, mapRoomPromotion),
    inclusions: mapOptional(inclusions, mapRoomInclusions('standard')) ?? [],
    bonusInclusions: mapOptional(bonusInclusions, mapRoomInclusions('bonus')) ?? [],
    luxPlusInclusions: mapOptional(luxPlusInclusions, mapRoomInclusions('luxPlus')) ?? [],
    bundleInclusions: mapOptional(bundleInclusions, mapRoomInclusions('standard')) ?? [],
    /* Metadata */
    defaultOrder,
    stats: mapStats(stats),
  }
}

// NB: This mapper (as well as transitive mappers and related Customer Portal types) was last
// cross-checked against `contract-svc-accommodation` v21.12.0.
export default function mapProperty({
  id,
  name,
  copy: {
    description,
    additionalDescription,
    highlights,
    facilities,
    locationDescription,
    tagline,
    // metaDescription, // This is unused; Customer Portal generates a generic meta description.
    finePrint,
    finePrintSubsections,
    taxesAndFees,
  },
  // type, // This is only used on the checkout page, not the offer page.
  logo,
  slug,
  rooms,
  images,
  reviews,
  location,
  locationTitle,
  amenityGroups,
  popularFacilities,
  groupedFacilities,
  flights: {
    destinationAirport,
    // cacheDisabled, // This is irrelevant for SPP; we always use the single cheapest flight endpoint.
    enabled: areFlightsEnabled,
    maxArrivalTime,
    minReturningDepartureTime,
    bundledWithFlightsOnly,
    // warning, // This is only used on the checkout page, not the offer page.
  },
  structuredFinePrint,
  stats,
  tags: {
    themes: themeTags,
    // urgency, // This is unused by SPP and bedbank offer pages (for now).
    location: locationTags,
    holidayTypes: holidayTypeTags,
    campaigns: campaignTags,
  },
  durations,
  marketedLos,
  walledGarden,
  // saleUnit, // This is unused (for now).
  singleUnit,
  isAgentHubExclusive,
  accommV2Bookable,
  promotion,
}: ServerPropertyContent, previewMode: boolean): App.Property.Property {
  return {
    /* Identifiers */
    id,
    slug,
    /* Configuration options */
    walledGarden,
    marketedLos,
    areFlightsEnabled: config.FLIGHT_ENABLED && (
      // NB: Agent Hub sets `FLIGHT_ENABLED`, but this was only ever intended for offers bundled
      // with Maldives charter flights. It matches `canHotelOfferFlightsBeEnabled`'s behaviour.
      !config.ENABLE_ONLY_HOTEL_OFFER_FLIGHTS_FOR_MALDIVES_CHARTS ||
      campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)
    ) && areFlightsEnabled,
    mustBundleWithFlights: !!bundledWithFlightsOnly,
    airportCode: destinationAirport ?? undefined,
    latestDepartingFlightArrivalTime: maxArrivalTime ?? undefined,
    earliestReturningFlightDepartureTime: minReturningDepartureTime ?? undefined,
    isAgentHubExclusive: config.agentHub.isEnabled && !!isAgentHubExclusive,
    accommV2Bookable: !!accommV2Bookable,
    rooms: arrayToObject(
      rooms,
      ({ id }) => id,
      (room, index) => mapRoom(room, {
        defaultOrder: index,
        previewMode,
      }),
    ),
    /* Content */
    name,
    headline: tagline ?? undefined,
    logo: mapOptional(logo, mapImage),
    images: images.map(mapImage),
    singleUnit: singleUnit ? mapSingleUnitContent(rooms) : undefined,
    description: mapOptional(description, mapMarkdown),
    additionalDescription: mapOptional(additionalDescription, mapMarkdown),
    location: mapLocation(location, locationTitle),
    locationDescription: mapOptional(locationDescription, mapMarkdown),
    highlights: mapOptional(highlights, mapMarkdown),
    popularFacilities: popularFacilities.map(mapPopularFacility),
    facilities: mapOptional(facilities, mapMarkdown),
    amenities: amenityGroups.map(mapAmenityGroup),
    structuredFacilities: mapOptional(groupedFacilities, x => mapNonEmptyArray(x, mapStructuredFacilityGroup)),
    finePrint: mapOptional(finePrint, mapMarkdown),
    finePrintSubsections: mapOptional(finePrintSubsections, x => mapNonEmptyArray(x, mapFinePrintSubsection)),
    structuredFinePrint: mapOptional(structuredFinePrint, x => mapStructuredFinePrint(x, themeTags)),
    taxesAndFeesContent: mapOptional(taxesAndFees, mapMarkdown),
    /* Marketed rates */
    promotion: mapOptional(promotion, mapPropertyPromotion),
    durationOptions: durations.map(mapDurationOption),
    /* Metadata */
    rating: mapOptional(reviews, mapRating),
    stats: mapStats(stats),
    campaignTags,
    holidayTypeTags,
    locationTags,
  }
}
